import type { InvivoBackgroundDescriptor, InvivoBackgroundTextureDescriptor } from "../types/invivo_scene";

function cube(url: string, coordinatesMode?: InvivoBackgroundTextureDescriptor["coordinatesMode"]): InvivoBackgroundTextureDescriptor {
  return {
    kind: "cube",
    url,
    coordinatesMode,
  };
}

function texture(
  url: string,
  options: {
    invertY: boolean;
    uScale?: number;
    vScale?: number;
    coordinatesMode?: InvivoBackgroundTextureDescriptor["coordinatesMode"];
  }
): InvivoBackgroundTextureDescriptor {
  return {
    kind: "texture",
    url,
    invertY: options.invertY,
    uScale: options.uScale ?? 1,
    vScale: options.vScale ?? 1,
    coordinatesMode: options.coordinatesMode,
  };
}

export function buildLegacyBackgroundDescriptor({
  backgroundValue,
  sharedAssetBaseUrl,
}: {
  backgroundValue: string;
  sharedAssetBaseUrl: string;
}): InvivoBackgroundDescriptor {
  const resolvedBackgroundValue =
    typeof backgroundValue === "string" && backgroundValue.length > 0 ? backgroundValue : "or";
  const drapes = resolvedBackgroundValue.startsWith("d-");
  const normalizedValue = resolvedBackgroundValue.replace(/^d-/, "").replace(/^#/, "");
  const isColor = /^[a-f0-9]{6}$/i.test(normalizedValue);
  const sharedAssetUrl = (relativePath: string): string =>
    new URL(relativePath.slice("shared/".length), sharedAssetBaseUrl).toString();

  if (normalizedValue === "none") {
    return {
      mode: "none",
      enabled: false,
    };
  }

  if (isColor) {
    return {
      mode: "color",
      enabled: true,
      emissiveColor: `#${normalizedValue}`,
      ambientTexture: drapes
        ? texture(sharedAssetUrl("shared/environments/drapes.png"), {
            invertY: false,
          })
        : undefined,
    };
  }

  if (normalizedValue === "or") {
    return {
      mode: "or_env",
      enabled: true,
      emissiveColor: "#000000",
      reflectionTexture: cube(sharedAssetUrl("shared/environments/OR_backdrop.env"), "INVCUBIC_MODE"),
      environmentTexture: cube(sharedAssetUrl("shared/environments/OR_lighting.env")),
    };
  }

  if (normalizedValue === "gp") {
    return {
      mode: "gp_env",
      enabled: true,
      emissiveColor: "#000000",
      reflectionTexture: texture(sharedAssetUrl("shared/environments/GP_backdrop.jpg"), {
        invertY: true,
        coordinatesMode: "FIXED_EQUIRECTANGULAR_MIRRORED_MODE",
      }),
      environmentTexture: cube(sharedAssetUrl("shared/environments/GP_lighting.env")),
    };
  }

  throw new Error(`Unsupported background preset "${normalizedValue}".`);
}
